import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Camera, ImagePlus, ScanText, X, Check } from "lucide-react";
import { toast } from "sonner";
import { authHeaders, handleUnauthorized } from "@/lib/pin";
import { Shell } from "@/components/Shell";
import { SECTIONS, formatAnswer } from "@/data/questionnaire";
import { createSubmission, upsertSubmission } from "@/lib/storage";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const buildSchema = () => JSON.stringify(SECTIONS.map((s) => ({
  sezione: s.title,
  domande: s.questions.map((q) => ({ id: q.id, testo: q.text, tipo: q.type, opzioni: q.options, min: q.min, max: q.max })),
})));

const normalize = (answers, ALL_Q) => {
  const out = {};
  for (const q of ALL_Q) {
    let v = answers?.[q.id];
    if (v === undefined || v === null || v === "") continue;
    if (q.type === "multiple") v = (Array.isArray(v) ? v : [v]).filter((o) => q.options.includes(o));
    else if (q.type === "single") { if (!q.options.includes(v)) continue; }
    else if (q.type === "scale" || q.type === "number") { v = Number(v); if (Number.isNaN(v)) continue; }
    else v = String(v);
    if (Array.isArray(v) && v.length === 0) continue;
    out[q.id] = v;
  }
  return out;
};

export default function PhotoImportPage() {
  const navigate = useNavigate();
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const ALL_Q = SECTIONS.flatMap((s) => s.questions);

  const addFiles = (list) => setFiles((f) => [...f, ...Array.from(list)].slice(0, 8));

  const read = async () => {
    if (!files.length) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("schema", buildSchema());
      files.forEach((f) => fd.append("files", f));
      const res = await fetch(`${API}/estrai-foto`, { method: "POST", headers: authHeaders(), body: fd });
      handleUnauthorized(res);
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || `Errore ${res.status}`);
      const data = await res.json();
      setResult({ respondent: data.respondent || "", answers: normalize(data.answers, ALL_Q), notes: data.notes || "" });
    } catch (e) {
      toast.error(`Lettura non riuscita: ${e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const create = () => {
    const sub = createSubmission(result.respondent || "Da modulo cartaceo", "live");
    upsertSubmission({ ...sub, answers: result.answers, notes: result.notes ? `Dubbi di lettura AI: ${result.notes}` : "" });
    toast.success("Compilazione creata: controlla e correggi le risposte");
    navigate(`/rapido/${sub.id}`);
  };

  const found = result ? Object.keys(result.answers).length : 0;

  return (
    <Shell header={<button className="btn btn-ghost !min-h-[44px] text-sm" onClick={() => navigate("/")} data-testid="photo-back-btn"><ArrowLeft size={16} /> Elenco</button>}>
      <div className="fade-up">
        <p className="eyebrow">Dal foglio al tablet</p>
        <h1 className="font-serif text-4xl sm:text-5xl font-bold tracking-tight text-[#2C221E] mt-2">Compila da foto</h1>
        <p className="text-[#5C4D46] mt-3">Fotografa le pagine del modulo cartaceo compilato (anche più foto). L'AI legge caselle e scrittura a mano, poi tu controlli e correggi.</p>

        {!result && (
          <div className="card p-6 sm:p-9 mt-8">
            <div className="grid sm:grid-cols-2 gap-4">
              <label className="choice justify-center cursor-pointer !min-h-[80px]" data-testid="photo-camera-label">
                <Camera size={22} className="text-[#C85A32]" /> Scatta una foto
                <input type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => addFiles(e.target.files)} data-testid="photo-camera-input" />
              </label>
              <label className="choice justify-center cursor-pointer !min-h-[80px]" data-testid="photo-gallery-label">
                <ImagePlus size={22} className="text-[#2D4030]" /> Scegli dalla galleria
                <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => addFiles(e.target.files)} data-testid="photo-file-input" />
              </label>
            </div>

            {files.length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-6" data-testid="photo-previews">
                {files.map((f, i) => (
                  <div key={i} className="relative rounded-xl overflow-hidden border border-[#E3D5C3] aspect-[3/4] bg-[#F3ECE1]">
                    <img src={URL.createObjectURL(f)} alt={`Pagina ${i + 1}`} className="w-full h-full object-cover" />
                    <button className="absolute top-2 right-2 w-8 h-8 rounded-full bg-white/90 grid place-items-center text-[#A83232]" onClick={() => setFiles((l) => l.filter((_, j) => j !== i))} aria-label="Rimuovi" data-testid={`photo-remove-${i}`}><X size={16} /></button>
                    <span className="absolute bottom-2 left-2 text-xs bg-white/90 rounded-full px-2 py-0.5">Pagina {i + 1}</span>
                  </div>
                ))}
              </div>
            )}

            <div className="flex justify-end mt-8">
              <button className="btn btn-primary text-lg !min-h-[64px] !px-9" onClick={read} disabled={!files.length || busy} data-testid="photo-read-btn">
                <ScanText size={20} /> {busy ? "Sto leggendo il modulo…" : `Leggi ${files.length || ""} ${files.length === 1 ? "foto" : "foto"}`}
              </button>
            </div>
          </div>
        )}

        {result && (
          <div className="card p-6 sm:p-9 mt-8" data-testid="photo-result">
            <div className="flex flex-wrap items-baseline justify-between gap-3">
              <h2 className="font-serif text-2xl sm:text-3xl font-semibold text-[#382E2B]">Ho letto {found} risposte su {ALL_Q.length}</h2>
              <button className="text-sm text-[#7A6B63] underline underline-offset-4" onClick={() => setResult(null)} data-testid="photo-retry-btn">Rifai le foto</button>
            </div>
            <label className="block mt-5">
              <span className="eyebrow">Chi ha risposto</span>
              <input className="field mt-2" value={result.respondent} onChange={(e) => setResult({ ...result, respondent: e.target.value })} placeholder="es. Maria e Giovanni" data-testid="photo-respondent-input" />
            </label>
            {result.notes && <p className="mt-4 rounded-xl bg-[#FDF6E6] border border-[#D99B26]/40 p-3 text-sm text-[#5C4D46]" data-testid="photo-notes">Dubbi di lettura: {result.notes}</p>}
            <dl className="grid gap-3 mt-6">
              {ALL_Q.map((q) => {
                const a = formatAnswer(q, result.answers[q.id]);
                return (
                  <div key={q.id} className="flex gap-3 text-sm">
                    <span className={`flex-none w-5 h-5 rounded-full grid place-items-center ${a ? "bg-[#2E6B40] text-white" : "bg-[#EAE0D0] text-[#8A7970]"}`}>{a ? <Check size={12} /> : "?"}</span>
                    <div><dt className="text-[#7A6B63]">{q.text}</dt><dd className={a ? "text-[#2C221E]" : "italic text-[#B0A096]"}>{a || "non letta"}</dd></div>
                  </div>
                );
              })}
            </dl>
            <div className="flex justify-end mt-8">
              <button className="btn btn-primary text-lg !min-h-[64px] !px-9" onClick={create} data-testid="photo-create-btn">
                <Check size={20} /> Crea la compilazione e controlla
              </button>
            </div>
          </div>
        )}
      </div>
    </Shell>
  );
}
